import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useGroupChatWS from "../../hooks/useGroupChatWS";
import { GROUP_CHAT_HUB_HTTP_URL } from "../../config/serviceConfig";
import ChatStream from "./ChatStream";
import MessageInput from "./MessageInput";
import PhaseIndicator from "./PhaseIndicator";
import RoomLobby from "./RoomLobby";
import ArchiveView from "./ArchiveView";
import ScienceExpoStage from "./ScienceExpoStage";
import GroupChatSidePanel from "./GroupChatSidePanel";
import Breadcrumb from "../Layout/Breadcrumb";
import { buildGroupChatThemeStyle } from "../../utils/brandTheme";
import { getPreferredLanguage } from "../../config/languages";
import { getLocalStorage, setLocalStorage } from "../../utils/storageCache";
import "./GroupChatPanel.css";

/* "正在说话"信号的兜底存活时间。发言方刷新、断线或标签页被杀会丢掉 speaking:false，
 * 没有这个上限，那枚高亮会一直亮着。取值只需大于一次正常发言的时长。 */
const SPEAKING_SIGNAL_TTL_MS = 15000;

/**
 * 群聊协作主面板
 * 参考 DiscussionPanel 结构，连接 GroupChatHub(:8020)
 */
const GroupChatPanel = ({
  isOpen,
  onClose,
  currentUser,
  roomId,
  setRoomId,
  projectName,
  setProjectName,
  currentPhase,
  setCurrentPhase,
  phaseLabel,
  setPhaseLabel,
  phases,
  setPhases,
  members,
  setMembers,
  agents,
  setAgents,
  messages,
  setMessages,
  streamingContent,
  setStreamingContent,
  roundsInPhase,
  setRoundsInPhase,
  maxRounds,
  setMaxRounds,
  status,
  setStatus,
  isWaiting,
  setIsWaiting,
  templateId,
  setTemplateId,
  myUserId,
  setMyUserId,
  groupChatWsRef,
  onReset,
  onArchiveSaved,
  onArchiveViewChange,
  onConnectionStatusChange,
  showNotification,
  headerAccessory = null,
}) => {
  const { t } = useTranslation();
  const [lobbyError, setLobbyError] = useState(null);
  const [viewArchive, setViewArchive] = useState(null); // { roomId, projectName } 或 null
  const [isDisconnected, setIsDisconnected] = useState(false);
  const [displayMode, setDisplayMode] = useState("normal"); // 大厅/创建房间模式: normal | expo
  const [roomDisplayMode, setRoomDisplayMode] = useState("normal"); // 房间实际运行模式
  const [clientViewMode, setClientViewMode] = useState("normal"); // 当前客户端界面模式
  // 仅控制展示舞台的视觉编排；不写入 WebSocket 协议，避免影响 expo 音视频运行链路。
  const [expoLayout, setExpoLayout] = useState("classic"); // classic | comprehensive
  const [competencyProfile, setCompetencyProfile] = useState(null);
  const [competencyHistory, setCompetencyHistory] = useState([]);
  const [silentTurnNotice, setSilentTurnNotice] = useState(null);
  const [robotBind, setRobotBind] = useState(null);
  const [scriptMode, setScriptMode] = useState(""); // "" | recording
  const [manualKeyNodes, setManualKeyNodes] = useState([]);
  const [activeNodeId, setActiveNodeId] = useState("");
  // 全局推理排队的轻量提示（不进聊天记录）：null | { status, position }
  const [aiQueueHint, setAiQueueHint] = useState(null);
  const streamAccumulatorRef = useRef({}); // agentId -> accumulated content
  const skipNextRoomStateMessagesRef = useRef(false); // 重连后跳过 room_state 的 messages 覆盖
  const pendingExpoLayoutRef = useRef(null); // 创建/加入时用户明确选择的布局优先于该房间的旧缓存
  const messageInputRef = useRef(null);
  const messagesWrapRef = useRef(null);

  const getLanguage = () => getPreferredLanguage();

  useEffect(() => {
    if (!roomId) return;
    const storageKey = `groupchat:expo-layout:${roomId}`;
    if (pendingExpoLayoutRef.current) {
      const requestedLayout = pendingExpoLayoutRef.current;
      pendingExpoLayoutRef.current = null;
      setExpoLayout(requestedLayout);
      setLocalStorage(storageKey, requestedLayout);
      return;
    }
    const storedLayout = getLocalStorage(storageKey);
    if (storedLayout === "comprehensive" || storedLayout === "classic") {
      setExpoLayout(storedLayout);
      return;
    }
    setLocalStorage(storageKey, expoLayout);
  }, [expoLayout, roomId]); // 只在切换房间时恢复布局，避免 room_state 覆盖用户的本地选择。

  useEffect(() => {
    if (roomId) {
      setLocalStorage(`groupchat:expo-layout:${roomId}`, expoLayout);
    }
  }, [expoLayout, roomId]);

  const toChatMessage = useCallback((m) => ({
    seq: m.seq,
    senderType: m.senderType || "student",
    senderId: m.senderId,
    senderName: m.senderName,
    senderRole: m.senderRole,
    content: m.content,
    timestamp: m.timestamp,
  }), []);

  const splitProjectName = (value) => {
    const raw = String(value || "").trim();
    const match = raw.match(/^(.*)（创建者：(.+)）$/);
    if (!match) return { title: raw, creator: "" };
    return { title: match[1].trim(), creator: match[2].trim() };
  };

  const handleRoomState = useCallback(
    (data) => {
      setRoomId(data.roomId);
      setProjectName(data.projectName || "");
      setCurrentPhase(data.currentPhase || "");
      setPhaseLabel(data.phaseLabel || "");
      setPhases(data.phases || []);
      setMembers(data.members || []);
      setAgents(data.agents || []);
      setRoundsInPhase(data.roundsInPhase ?? 0);
      setMaxRounds(data.maxRounds ?? 8);
      setMyUserId(data.myUserId || "");
      setRobotBind(data.robot || null);
      setStatus("active");
      setIsWaiting(false);
      setIsDisconnected(false);
      setRoomDisplayMode(data.roomDisplayMode || data.displayMode || "normal");
      setClientViewMode(data.clientViewMode || data.displayMode || "normal");
      setScriptMode(data.scriptMode || "");
      setCompetencyProfile(data.competencyProfile || null);
      setCompetencyHistory(data.competencyProfile?.evaluatedSeq
        ? [{ ...data.competencyProfile, turn: 1 }]
        : []);
      if (data.recentMessages && data.recentMessages.length > 0 && !skipNextRoomStateMessagesRef.current) {
        setMessages(
          data.recentMessages.map(toChatMessage)
        );
      }
      skipNextRoomStateMessagesRef.current = false;
    },
    [
      setRoomId,
      setProjectName,
      setCurrentPhase,
      setPhaseLabel,
      setPhases,
      setMembers,
      setAgents,
      setMessages,
      setMyUserId,
      setRoundsInPhase,
      setMaxRounds,
      setStatus,
      setIsWaiting,
      toChatMessage,
    ]
  );

  /* 谁正按住麦克风。纯 UI 信号，不进 messages、不落库。
   * 没有它，其他客户端只能等 ASR 出结果、student_speak 回流才知道有人说过话。 */
  const [speakingMemberIds, setSpeakingMemberIds] = useState(() => new Set());
  const speakingTimersRef = useRef(new Map());
  const setMemberSpeaking = useCallback((userId, speaking) => {
    if (!userId) return;
    const timers = speakingTimersRef.current;
    const running = timers.get(userId);
    if (running) {
      clearTimeout(running);
      timers.delete(userId);
    }
    setSpeakingMemberIds((prev) => {
      if (prev.has(userId) === speaking) return prev;
      const next = new Set(prev);
      if (speaking) next.add(userId);
      else next.delete(userId);
      return next;
    });
    if (!speaking) return;
    timers.set(userId, setTimeout(() => {
      timers.delete(userId);
      setSpeakingMemberIds((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }, SPEAKING_SIGNAL_TTL_MS));
  }, []);
  const handleMemberSpeaking = useCallback(
    (data) => setMemberSpeaking(data?.userId, !!data?.speaking),
    [setMemberSpeaking]
  );
  useEffect(() => () => {
    for (const timer of speakingTimersRef.current.values()) clearTimeout(timer);
    speakingTimersRef.current.clear();
  }, []);

  const handleGroupChatMsg = useCallback(
    (data) => {
      if (data.subType === "student_speak") {
        // 发言已成文，这一轮的"正在说话"必然结束；不依赖对端的 speaking:false 送达
        setMemberSpeaking(data.senderId, false);
        setSilentTurnNotice(null);
        setMessages((prev) => [
          ...prev,
          {
            seq: data.seq,
            senderType: "student",
            senderId: data.senderId,
            senderName: data.senderName,
            senderRole: data.senderRole,
            content: data.content,
          },
        ]);
      } else if (data.subType === "system_hint") {
        setSilentTurnNotice(null);
        setMessages((prev) => [
          ...prev,
          {
            seq: data.seq,
            senderType: "system",
            senderName: data.senderName || "系统",
            content: data.content || "",
          },
        ]);
      }
    },
    [setMessages, setMemberSpeaking]
  );

  const handleAgentSpeakStream = useCallback(
    (data) => {
      // AI 已开始输出时清掉排队提示和静默引导
      setAiQueueHint(null);
      setSilentTurnNotice(null);
      if (data.isEnd) {
        const fullContent =
          data.replace
            ? (data.chunk || "")
            : (streamAccumulatorRef.current[data.agentId] || "") + (data.chunk || "");
        streamAccumulatorRef.current[data.agentId] = "";
        setStreamingContent((prev) => {
          const next = { ...prev };
          delete next[data.agentId];
          return next;
        });
        setMessages((prev) => [
          ...prev,
          {
            seq: data.seq,
            senderType: "agent",
            senderId: data.agentId,
            senderName: data.agentName,
            content: fullContent,
            timestamp: data.timestamp,
            embodied: !!data.embodied,
            speechKind: data.speechKind || "",
            gestureTag: data.gestureTag || "",
          },
        ]);
      } else {
        const chunk = data.chunk || "";
        streamAccumulatorRef.current[data.agentId] =
          (streamAccumulatorRef.current[data.agentId] || "") + chunk;
        setStreamingContent((prev) => ({
          ...prev,
          [data.agentId]: {
            agentName: data.agentName,
            content: (prev[data.agentId]?.content || "") + chunk,
            embodied: !!data.embodied,
            speechKind: data.speechKind || prev[data.agentId]?.speechKind || "",
          },
        }));
      }
    },
    [setStreamingContent, setMessages]
  );

  const handleHostReadReceipt = useCallback(
    (data) => {
      setAiQueueHint(null);
      const kind = data?.kind || "natural_continue";
      const label = data?.label || (kind === "low_information" ? "再说具体一点" : "继续讨论");
      const text =
        data?.detail ||
        (kind === "low_information"
          ? "这句话信息有点少，试着说出你的想法和理由；想请教专家，可以直接提问或点名某位专家。"
          : "已收到你的发言。想让专家回应，把你的想法和理由说得更具体一些，或者直接提问、点名某位专家。");
      setSilentTurnNotice({ id: Date.now(), kind, label, text });
    },
    []
  );

  const handlePhaseChange = useCallback(
    (data) => {
      setSilentTurnNotice(null);
      setCurrentPhase(data.phase || "");
      setPhaseLabel(data.phaseLabel || data.phase || "");
      setMessages((prev) => [
        ...prev,
        {
          type: "phase",
          content: data.phaseDesc || data.phaseLabel,
        },
      ]);
    },
    [setCurrentPhase, setPhaseLabel, setMessages]
  );

  const handleTrainingCompleted = useCallback(
    (data) => {
      setSilentTurnNotice(null);
      setStatus("completed");
      setIsWaiting(false);
      setMessages((prev) => [
        ...prev,
        {
          type: "training_completed",
          content: data.message || "本训练已完成，所有阶段已结束",
          phaseLabel: data.phaseLabel,
        },
      ]);
    },
    [setStatus, setIsWaiting, setMessages]
  );

  const handleMemberStatus = useCallback(
    (data) => {
      if (data.action === "join") {
        setMembers((prev) => {
          const exists = prev.some((m) => m.userId === data.userId);
          if (exists) return prev.map((m) => (m.userId === data.userId ? { ...m, roleName: data.roleName, isOnline: true } : m));
          return [...prev, { userId: data.userId, username: data.username, roleName: data.roleName, isOnline: true }];
        });
      } else if (data.action === "leave") {
        setMembers((prev) =>
          prev.map((m) =>
            m.userId === data.userId ? { ...m, isOnline: false } : m
          )
        );
      }
    },
    [setMembers]
  );

  const handleError = useCallback(
    (msg) => {
      setLobbyError(msg);
      setMessages((prev) => [...prev, { type: "error", content: msg }]);
      showNotification?.(msg || "团队协作发生错误", "error");
      setIsWaiting(false);
      if (status === "creating" || status === "joining") {
        setStatus("idle");
      }
    },
    [setMessages, setIsWaiting, showNotification, status]
  );

  const handleRoomCreated = useCallback(
    (data) => {
      setRoomId(data.roomId);
      setStatus("active");
      setIsWaiting(false);
      if (data.restoredFrom && data.roomId) {
        fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/rooms/${data.roomId}/messages`)
          .then((res) => res.json())
          .then((payload) => {
            if (payload.error) {
              throw new Error(payload.error);
            }
            setMessages((payload.messages || []).map(toChatMessage));
          })
          .catch((err) => {
            setMessages((prev) => [
              ...prev,
              { type: "error", content: err.message || "历史消息加载失败" },
            ]);
          });
      }
    },
    [setMessages, setRoomId, setStatus, setIsWaiting, toChatMessage]
  );

  const handleRoundUpdate = useCallback(
    (data) => {
      if (data.roundsInPhase != null) setRoundsInPhase(data.roundsInPhase);
      if (data.maxRounds != null) setMaxRounds(data.maxRounds);
    },
    [setRoundsInPhase, setMaxRounds]
  );

  const handleScriptStatus = useCallback((data) => {
    console.log("[GroupChatHub] 录课脚本状态:", data);
  }, []);

  const handleReconnectResp = useCallback(
    (data) => {
      setIsDisconnected(false);
      skipNextRoomStateMessagesRef.current = true; // 后续 room_state 不覆盖 messages（已合并 missedMessages）
      if (data.missedMessages && data.missedMessages.length > 0) {
        setMessages((prev) => {
          const existingSeqs = new Set(prev.map((m) => m.seq).filter((s) => s != null));
          const toAdd = data.missedMessages.filter((m) => m.seq != null && !existingSeqs.has(m.seq));
          return [...prev, ...toAdd.map(toChatMessage)];
        });
      }
    },
    [setMessages, setIsDisconnected, toChatMessage]
  );

  const handleDisconnect = useCallback(() => {
    if (status === "active" || status === "completed") {
      setIsDisconnected(true);
      showNotification?.("团队协作连接已断开，可点击重新连接继续", "warning");
    }
  }, [showNotification, status]);

  const handleArchiveSaved = useCallback(
    (msg) => {
      const message = msg || "存档已保存";
      setMessages((prev) => [...prev, { type: "system", content: "✅ " + message }]);
      showNotification?.(message, "success");
      onArchiveSaved?.();
    },
    [onArchiveSaved, showNotification]
  );

  const handleAIQueueStatus = useCallback((data) => {
    if (!data || !data.status) return;
    if (data.status === "queued" && data.position > 0) {
      setAiQueueHint({ status: "queued", position: data.position });
      return;
    }
    // started：若仍无排队文案，保留本地「处理中…」直到 AI 开始输出
    if (data.status === "started") {
      setAiQueueHint((prev) => (prev?.status === "queued" ? { status: "processing" } : prev || { status: "processing" }));
      return;
    }
    // cancelled：清除
    setAiQueueHint(null);
  }, []);

  const handleCompetencyUpdate = useCallback((data) => {
    const profile = data?.competencyProfile || null;
    setCompetencyProfile(profile);
    if (!profile?.evaluatedSeq) return;
    setCompetencyHistory((previous) => {
      const existing = previous.find((item) => item?.evaluatedSeq === profile.evaluatedSeq);
      const turn = existing?.turn || previous.reduce((max, item) => Math.max(max, Number(item?.turn) || 0), 0) + 1;
      const withoutCurrent = previous.filter((item) => item?.evaluatedSeq !== profile.evaluatedSeq);
      return [...withoutCurrent, { ...profile, turn }].slice(-3);
    });
  }, []);

  const aiQueueStatusText = useMemo(() => {
    if (!aiQueueHint) return "";
    if (aiQueueHint.status === "queued") {
      const n = Number(aiQueueHint.position) || 0;
      if (n <= 0) return "";
      return `前方约 ${n} 位`;
    }
    if (aiQueueHint.status === "processing") return "处理中…";
    return "";
  }, [aiQueueHint]);

  const getReconnectParams = useCallback(() => {
    if (!roomId || !myUserId) return null;
    if (!(status === "active" || status === "completed")) return null;
    const lastSeq = messages.reduce((max, m) => Math.max(max, m.seq || 0), 0);
    return {
      roomId,
      userId: myUserId,
      lastSeq,
      clientViewMode: clientViewMode || "normal",
    };
  }, [roomId, myUserId, status, messages, clientViewMode]);

  const { connect, send, sendReconnect, disconnect } = useGroupChatWS({
    wsRef: groupChatWsRef,
    onRoomState: handleRoomState,
    onRoomCreated: handleRoomCreated,
    onGroupChatMsg: handleGroupChatMsg,
    onAgentSpeakStream: handleAgentSpeakStream,
    onHostReadReceipt: handleHostReadReceipt,
    onPhaseChange: handlePhaseChange,
    onTrainingCompleted: handleTrainingCompleted,
    onMemberStatus: handleMemberStatus,
    onMemberSpeaking: handleMemberSpeaking,
    onRoundUpdate: handleRoundUpdate,
    onScriptStatus: handleScriptStatus,
    onReconnectResp: handleReconnectResp,
    onDisconnect: handleDisconnect,
    onError: handleError,
    onArchiveSaved: handleArchiveSaved,
    onConnectionStatusChange,
    onAIQueueStatus: handleAIQueueStatus,
    onCompetencyUpdate: handleCompetencyUpdate,
    onRobotReleased: (data) => setRobotBind(data?.robot || { bound: false, deviceId: "", speaker: "" }),
    getReconnectParams,
  });

  const handleCreateRoomFromLobby = useCallback(
    (opts) => {
      setLobbyError(null);
      const nextMode = opts.displayMode || "normal";
      const nextExpoLayout = opts.expoLayout === "comprehensive" ? "comprehensive" : "classic";
      setRoomDisplayMode(nextMode);
      setClientViewMode(nextMode);
      pendingExpoLayoutRef.current = nextExpoLayout;
      setExpoLayout(nextExpoLayout);
      connect(() => {
        send({
          type: "create_room",
          templateId: opts.templateId,
          userId: opts.userId,
          studentId: opts.userId,
          username: opts.username,
          displayName: opts.displayName || opts.creatorName || opts.username,
          creatorName: opts.creatorName || opts.username,
          userType: opts.userType,
          orgId: opts.orgId,
          assignmentId: opts.assignmentId,
          lang: opts.lang,
          roleName: opts.roleName || "",
          displayMode: opts.displayMode || "normal",
          clientViewMode: opts.displayMode || "normal",
          scriptMode: opts.scriptMode || "",
          scriptComd: opts.scriptComd || "",
          expoLayout: nextExpoLayout,
          competencyEvaluationEnabled: nextExpoLayout === "comprehensive",
          deviceId: opts.deviceId || "",
        });
        setStatus("creating");
        setIsWaiting(true);
      });
    },
    [connect, send]
  );

  const handleJoinRoomFromLobby = useCallback(
    (opts) => {
      setLobbyError(null);
      setClientViewMode(opts.clientViewMode || "normal");
      const nextExpoLayout = opts.expoLayout === "comprehensive" ? "comprehensive" : "classic";
      pendingExpoLayoutRef.current = nextExpoLayout;
      setExpoLayout(nextExpoLayout);
      connect(() => {
        send({
          type: "join_room",
          roomId: opts.roomId,
          userId: opts.userId,
          studentId: opts.userId,
          username: opts.username,
          displayName: opts.displayName || opts.username,
          userType: opts.userType,
          roleName: opts.roleName || "",
          clientViewMode: opts.clientViewMode || "normal",
        });
        setStatus("joining");
        setIsWaiting(true);
      });
    },
    [connect, send]
 );

  const handleResumeRoomFromLobby = useCallback(
    (opts) => {
      setLobbyError(null);
      const nextMode = opts.displayMode || "normal";
      const nextExpoLayout = opts.expoLayout === "comprehensive" ? "comprehensive" : "classic";
      setRoomDisplayMode(nextMode);
      setClientViewMode(nextMode);
      pendingExpoLayoutRef.current = nextExpoLayout;
      setExpoLayout(nextExpoLayout);
      setMessages([]);
      streamAccumulatorRef.current = {};
      connect(() => {
        send({
          type: "resume_room",
          sourceRoomId: opts.sourceRoomId,
          userId: opts.userId,
          studentId: opts.userId,
          username: opts.username,
          displayName: opts.displayName || opts.creatorName || opts.username,
          creatorName: opts.creatorName || opts.username,
          userType: opts.userType,
          orgId: opts.orgId,
          lang: opts.lang,
          roleName: opts.roleName || "",
          displayMode: opts.displayMode || "normal",
          clientViewMode: opts.displayMode || "normal",
          scriptMode: opts.scriptMode || "",
          scriptComd: opts.scriptComd || "",
          expoLayout: nextExpoLayout,
          competencyEvaluationEnabled: nextExpoLayout === "comprehensive",
        });
        setStatus("joining");
        setIsWaiting(true);
      });
    },
    [connect, send, setMessages, setStatus, setIsWaiting]
  );

  const handleViewArchive = useCallback((roomId, roomDetail) => {
    const projectName = roomDetail?.projectName || roomId;
    setViewArchive({
      roomId,
      projectName,
    });
    onArchiveViewChange?.({ roomId, projectName });
  }, [onArchiveViewChange]);

  const handleReconnect = useCallback(() => {
    const lastSeq = messages.reduce((max, m) => Math.max(max, m.seq || 0), 0);
    connect(() => {
      sendReconnect(roomId, myUserId, lastSeq, clientViewMode);
      setIsDisconnected(false);
    });
  }, [clientViewMode, connect, sendReconnect, roomId, myUserId, messages]);

  const handleSendMessage = useCallback(
    (payload) => {
      if (!roomId) return;
      // 发送后立即给出输入区轻量状态；真排队时会被「前方约 N 位」覆盖
      setSilentTurnNotice(null);
      setAiQueueHint({ status: "processing" });
      send({
        type: "student_speak",
        roomId,
        content: payload.content,
        mentions: payload.mentions || [],
      });
    },
    [roomId, send]
  );

  /* 上报自己是否正按住麦克风。服务端只转发不存状态，所以失败无需重试：
   * 下一次按下/松开自然会纠正，且对端有 TTL 兜底。 */
  const handleSpeakingStateChange = useCallback(
    (speaking) => {
      if (!roomId) return;
      send({ type: "speaking_state", roomId, speaking: !!speaking });
    },
    [roomId, send]
  );

  useEffect(() => {
    if (!(clientViewMode === "expo" && scriptMode === "recording" && roomId)) return undefined;
    const isEditableTarget = (target) => {
      if (!target || target === document.body) return false;
      const tag = String(target.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" ||
        target.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.repeat) return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      send({
        type: "script_control",
        roomId,
        action: e.key === "ArrowRight" ? "next" : "prev",
      });
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [clientViewMode, roomId, scriptMode, send]);

  const handleSaveArchive = useCallback(() => {
    if (!roomId) return;
    if (send({ type: "save_archive", roomId, userId: myUserId })) {
      setMessages((prev) => [...prev, { type: "system", content: "正在保存存档..." }]);
    }
  }, [myUserId, roomId, send]);

  const handleMention = useCallback((id, name) => {
    messageInputRef.current?.insertMention?.(name);
  }, []);

  const handleMarkKeyNode = useCallback((node) => {
    setManualKeyNodes((prev) => {
      if (!node?.messageDomId || prev.some((item) => item.messageDomId === node.messageDomId)) {
        return prev;
      }
      return [...prev, { ...node, id: `manual-${node.messageDomId}` }].slice(-12);
    });
  }, []);

  const currentPhaseConfig = phases.find((p) => p.phaseId === currentPhase || p.name === phaseLabel);
  const activeAgentIds = currentPhaseConfig?.activeAgents || [];
  const activeStreamingAgentIds = useMemo(
    () => Object.keys(streamingContent || {}),
    [streamingContent]
  );
  const autoKeyNodes = useMemo(() => {
    const importantKeywordPattern = /结论|方案|风险|错误|完成|总结|目标|需求|选型|实现|验证/;
    return messages
      .map((msg, index) => {
        const messageDomId = msg.seq != null ? `seq-${msg.seq}-${index}` : `idx-${index}`;
        const rawContent = String(msg.content || "").replace(/\s+/g, " ").trim();
        if (msg.type === "phase") {
          return {
            id: `node-phase-${messageDomId}`,
            messageDomId,
            title: rawContent || "阶段切换",
            meta: "阶段节点",
          };
        }
        if (msg.type === "training_completed") {
          return {
            id: `node-completed-${messageDomId}`,
            messageDomId,
            title: rawContent || "训练完成",
            meta: "完成节点",
          };
        }
        if (msg.type === "error") {
          return {
            id: `node-error-${messageDomId}`,
            messageDomId,
            title: rawContent.slice(0, 32) || "异常提示",
            meta: "错误提示",
          };
        }
        if ((msg.senderType === "agent" || msg.senderType === "host") && importantKeywordPattern.test(rawContent)) {
          return {
            id: `node-agent-${messageDomId}`,
            messageDomId,
            title: rawContent.slice(0, 32) || "智能体关键回复",
            meta: msg.senderName || "智能体",
          };
        }
        return null;
      })
      .filter(Boolean)
      .slice(-12);
  }, [messages]);
  const keyNodes = useMemo(() => {
    const seen = new Set();
    return [...manualKeyNodes, ...autoKeyNodes]
      .filter((node) => {
        if (!node?.messageDomId || seen.has(node.messageDomId)) return false;
        seen.add(node.messageDomId);
        return true;
      })
      .slice(-12);
  }, [autoKeyNodes, manualKeyNodes]);

  useEffect(() => {
    const root = messagesWrapRef.current;
    if (!root || keyNodes.length === 0) {
      setActiveNodeId("");
      return undefined;
    }

    const nodeIdSet = new Set(keyNodes.map((node) => node.messageDomId));
    const elements = Array.from(root.querySelectorAll("[data-groupchat-message-id]"))
      .filter((element) => nodeIdSet.has(element.dataset.groupchatMessageId));

    if (elements.length === 0) {
      setActiveNodeId("");
      return undefined;
    }

    const evaluateActiveNode = () => {
      const rootRect = root.getBoundingClientRect();
      const visibleElements = elements
        .map((element) => ({
          element,
          rect: element.getBoundingClientRect(),
        }))
        .filter(({ rect }) => rect.bottom >= rootRect.top && rect.top <= rootRect.bottom)
        .sort((a, b) => a.rect.top - b.rect.top);

      if (visibleElements.length > 0) {
        setActiveNodeId(visibleElements[0].element.dataset.groupchatMessageId || "");
        return;
      }

      const lastAbove = elements
        .map((element) => ({
          element,
          rect: element.getBoundingClientRect(),
        }))
        .filter(({ rect }) => rect.top < rootRect.top)
        .sort((a, b) => b.rect.top - a.rect.top)[0];
      setActiveNodeId(lastAbove?.element.dataset.groupchatMessageId || "");
    };

    evaluateActiveNode();

    if (!("IntersectionObserver" in window)) {
      root.addEventListener("scroll", evaluateActiveNode, { passive: true });
      window.addEventListener("resize", evaluateActiveNode);
      return () => {
        root.removeEventListener("scroll", evaluateActiveNode);
        window.removeEventListener("resize", evaluateActiveNode);
      };
    }

    const observer = new IntersectionObserver(evaluateActiveNode, {
      root,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [keyNodes, messages.length]);

  const handleJumpToNode = useCallback((messageDomId) => {
    if (!messageDomId) return;
    setActiveNodeId(messageDomId);
    const target = messagesWrapRef.current?.querySelector(`[data-groupchat-message-id="${messageDomId}"]`)
      || document.querySelector(`[data-groupchat-message-id="${messageDomId}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (target) {
      target.classList.add("node-highlight");
      window.setTimeout(() => target.classList.remove("node-highlight"), 1400);
    }
  }, []);
  const isInRoomView = status === "creating" || status === "joining" || status === "active" || status === "completed";
  const isExpoRoomView = clientViewMode === "expo" && isInRoomView && !viewArchive;
  const isExpoRoomInNormalView = roomDisplayMode === "expo" && clientViewMode !== "expo" && isInRoomView && !viewArchive;

  const handleBackClick = useCallback(() => {
    if (viewArchive) {
      setViewArchive(null);
      onArchiveViewChange?.(null);
      return;
    }
    if (isInRoomView) {
      disconnect();
      onReset?.();
      setLobbyError(null);
      setViewArchive(null);
      onArchiveViewChange?.(null);
      setIsDisconnected(false);
      setManualKeyNodes([]);
      streamAccumulatorRef.current = {};
      return;
    }
    onClose?.();
  }, [disconnect, isInRoomView, onArchiveViewChange, onClose, onReset, viewArchive]);

  const handleClosePanel = useCallback(() => {
    setViewArchive(null);
    onArchiveViewChange?.(null);
    onClose?.();
  }, [onArchiveViewChange, onClose]);

  const brandThemeStyle = useMemo(
    () => buildGroupChatThemeStyle(currentUser?.branding),
    [
      currentUser?.branding?.bgImageUrl,
      currentUser?.branding?.themeColor,
    ]
  );

  if (!isOpen) return null;
  const projectTitleParts = splitProjectName(projectName);
  const archiveTitleParts = splitProjectName(viewArchive?.projectName || "");
  const groupChatBreadcrumbItems = [
    "邮百工",
    t("mode_groupchat"),
    viewArchive
      ? "历史记录"
      : isInRoomView
        ? (projectTitleParts.title || projectName || "当前项目")
        : "团队协作主页",
    viewArchive ? (archiveTitleParts.title || viewArchive.projectName || "") : "",
  ];

  return (
    <div
      className={`groupchat-panel-fullscreen ${isExpoRoomView ? "groupchat-panel-expo" : ""}`}
      style={brandThemeStyle}
    >
      {isExpoRoomView && headerAccessory ? (
        <div className="groupchat-timer-float">{headerAccessory}</div>
      ) : null}
      {!isExpoRoomView && (
      <div className="groupchat-header">
        <div className="groupchat-header-left">
          <button className="groupchat-back-btn" onClick={handleBackClick}>
            {isInRoomView || viewArchive ? "返回团队协作主页" : t("groupchat.back")}
          </button>
          {(isInRoomView || viewArchive) && (
            <button className="groupchat-close-btn" onClick={handleClosePanel}>
              关闭
            </button>
          )}
          <h2>{t("groupchat.title")}</h2>
          <Breadcrumb
            items={groupChatBreadcrumbItems}
            className="groupchat-breadcrumb"
          />
          {((viewArchive && archiveTitleParts.creator) || (!viewArchive && projectTitleParts.creator)) && (
            <span className="groupchat-project-creator">
              创建者：{viewArchive ? archiveTitleParts.creator : projectTitleParts.creator}
            </span>
          )}
        </div>
        <div className="groupchat-header-right">
          {headerAccessory && isInRoomView && !viewArchive ? headerAccessory : null}
          {isDisconnected && (status === "active" || status === "completed") && (
            <button className="groupchat-reconnect-btn" onClick={handleReconnect}>
              {t("groupchat.reconnect") || "重新连接"}
            </button>
          )}
          {(status === "active" || status === "completed") && !isDisconnected && (
            <PhaseIndicator
              currentPhase={currentPhase}
              phaseLabel={phaseLabel}
              phases={phases}
            />
          )}
        </div>
      </div>
      )}

      <div className={`groupchat-body ${isExpoRoomView ? "groupchat-body-expo" : ""}`}>
        {viewArchive ? (
          <ArchiveView
            roomId={viewArchive.roomId}
            projectName={viewArchive.projectName}
            currentUser={currentUser}
            onBack={() => {
              setViewArchive(null);
              onArchiveViewChange?.(null);
            }}
          />
        ) : status === "idle" ? (
          <div className="groupchat-welcome">
            <h3>{t("groupchat.welcome_title")}</h3>
            <p>{t("groupchat.welcome_desc")}</p>
            <RoomLobby
              currentUser={currentUser}
              onCreateRoom={handleCreateRoomFromLobby}
              onJoinRoom={handleJoinRoomFromLobby}
              onResumeRoom={handleResumeRoomFromLobby}
              onViewArchive={handleViewArchive}
              isCreating={status === "creating" && isWaiting}
              isJoining={status === "joining" && isWaiting}
              error={lobbyError}
              clearError={() => setLobbyError(null)}
              displayMode={displayMode}
              onDisplayModeChange={setDisplayMode}
              expoLayout={expoLayout}
              onExpoLayoutChange={setExpoLayout}
              scriptMode={scriptMode}
              onScriptModeChange={setScriptMode}
              initialTemplateId={templateId}
            />
          </div>
        ) : null}

        {(status === "creating" || status === "joining" || status === "active" || status === "completed") && (
          clientViewMode === "expo" ? (
            <ScienceExpoStage
              projectName={projectName}
              scriptMode={scriptMode}
              agents={agents}
              members={members}
              myUserId={myUserId}
              messages={messages}
              streamingContent={streamingContent}
              currentPhase={currentPhase}
              phaseLabel={phaseLabel}
              phases={phases}
              activeAgentIds={activeAgentIds}
              status={status}
              isDisconnected={isDisconnected}
              onReconnect={handleReconnect}
              onSendMessage={handleSendMessage}
              onSaveArchive={handleSaveArchive}
              onBack={handleBackClick}
              onClose={onClose}
              currentUser={currentUser}
              brandThemeStyle={brandThemeStyle}
              queueStatusText={aiQueueStatusText}
              layoutVariant={expoLayout}
              competencyProfile={competencyProfile}
              competencyHistory={competencyHistory}
              silentTurnNotice={silentTurnNotice}
              speakingMemberIds={speakingMemberIds}
              onSpeakingStateChange={handleSpeakingStateChange}
              robotBind={robotBind}
              onReleaseRobot={() => {
                if (!roomId) return;
                const ok = window.confirm("释放后，这台机器人可被其他房间选用。本房间继续，数字人改由本机出声。");
                if (!ok) return;
                send({ type: "release_device", roomId });
              }}
            />
          ) : (
            <>
            <div className="groupchat-chat">
              <div className="groupchat-messages-wrap" ref={messagesWrapRef}>
                {isExpoRoomInNormalView && (
                  <div className="groupchat-system-msg">
                    当前房间按展示模式运行，你正在使用普通聊天界面。
                  </div>
                )}
                {(status === "creating" || status === "joining") && isWaiting && (
                  <div className="groupchat-system-msg">
                    {status === "creating" ? t("groupchat.creating_room") : t("groupchat.lobby.joining")}
                  </div>
                )}
                <ChatStream
                  messages={messages}
                  streamingContent={streamingContent}
                  myUserId={myUserId}
                  onMarkKeyNode={handleMarkKeyNode}
                  silentTurnNotice={silentTurnNotice}
                />
              </div>

              {status === "active" && (
                <MessageInput
                  ref={messageInputRef}
                  onSend={handleSendMessage}
                  members={members}
                  agents={agents}
                  disabled={isWaiting}
                  statusHint={aiQueueStatusText}
                />
              )}
              {status === "completed" && (
                <div className="groupchat-training-completed-banner">
                  <span className="banner-icon">✓</span>
                  <span className="banner-text">{t("groupchat.training_completed") || "本训练已完成，所有阶段已结束"}</span>
                </div>
              )}
            </div>
            <GroupChatSidePanel
              currentUser={currentUser}
              members={members}
              agents={agents}
              myUserId={myUserId}
              status={status}
              roomId={roomId}
              activeAgentIds={activeAgentIds}
              activeStreamingAgentIds={activeStreamingAgentIds}
              keyNodes={keyNodes}
              activeNodeId={activeNodeId}
              onMention={status === "active" ? handleMention : undefined}
              onSaveArchive={handleSaveArchive}
              onViewArchive={handleViewArchive}
              onJumpToNode={handleJumpToNode}
            />
            </>
          )
        )}
      </div>
    </div>
  );
};

export default GroupChatPanel;
